import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Space,
  Descriptions,
  Tag,
  Divider,
  message,
  Spin,
  Modal,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Planogram, PlanogramStatus } from '../../types/storeGroup';
import StoreGroupService from '../../services/storeGroupService';
import PlanogramEditor from './components/PlanogramEditor';

const STATUS_COLORS: Record<PlanogramStatus, string> = {
  Draft: 'orange',
  PendingReview: 'blue',
  Approved: 'green',
  Rejected: 'red',
};

const PlanogramDetail: React.FC = () => {
  const { storeGroupCode, storeId, code } = useParams<{
    storeGroupCode: string;
    storeId?: string;
    code: string;
  }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [planogram, setPlanogram] = useState<Planogram | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const storeGroupService = StoreGroupService;

  useEffect(() => {
    if (code) {
      fetchPlanogramDetail(code);
    }
  }, [code]);

  const fetchPlanogramDetail = async (code: string) => {
    setLoading(true);
    try {
      // 注意：实际的Service只有getPlanogram方法，这里需要根据实际情况调整
      // 暂时使用模拟数据或者修改Service接口
      const data = {
        id: code,
        code: code,
        name: 'Sample Planogram',
        storeGroupId: storeGroupCode || '',
        displayGroupCode: 'DG001',
        layoutName: 'Standard Layout',
        width: 800,
        height: 600,
        shelfCount: 4,
        productCount: 16,
        status: 'Draft' as PlanogramStatus,
        layout: { shelves: [], width: 800, height: 600 },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        importTime: new Date().toISOString(),
        importedBy: 'system',
        fileName: 'sample.xlsx',
        remark: 'Sample planogram for testing'
      };
      setPlanogram(data);
    } catch (error) {
      message.error(t('planogramDetail.fetchFailed'));
      console.error('Failed to fetch planogram details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (storeId) {
      navigate(`/store-group/${storeGroupCode}/store/${storeId}`);
    } else {
      navigate(`/store-group/${storeGroupCode}`);
    }
  };

  const handleEdit = () => {
    if (storeId) {
      navigate(`/store-group/${storeGroupCode}/store/${storeId}/planogram/${code}/edit`);
    } else {
      navigate(`/store-group/${storeGroupCode}/planogram/${code}/edit`);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: t('planogramDetail.confirmDelete'),
      content: t('planogram.confirmDeleteMessage'),
      okText: t('common.confirm'),
      okType: 'danger',
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          // Note: The Service does not have a deletePlanogram method, using simulated deletion here
          // await storeGroupService.deletePlanogram(code!);
          message.success(t('common.deleteSuccess'));
          handleBack();
        } catch (error) {
          message.error(t('common.deleteFailed'));
          console.error('Delete failed:', error);
        }
      },
    });
  };

  const handleExport = async () => {
    try {
      // Note: The Service does not have an exportPlanogram method, using simulated export here
      // await storeGroupService.exportPlanogram(code!);
      message.success(t('common.exportSuccess'));
    } catch (error) {
      message.error(t('common.exportFailed'));
      console.error('Export failed:', error);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!planogram) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <h2>{t('planogramDetail.notFound')}</h2>
        <Button type="primary" onClick={handleBack}>{t('common.back')}</Button>
      </div>
    );
  }

  return (
    <div className="planogram-detail">
      <div className="page-header">
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
            {t('common.back')}
          </Button>
          <h1>{t('planogramDetail.title')}</h1>
        </Space>
        <Space>
          {planogram.status !== 'Approved' && (
            <Button
              icon={<EditOutlined />}
              onClick={handleEdit}
            >
              {t('common.edit')}
            </Button>
          )}
          {planogram.status !== 'Approved' && (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleDelete}
            >
              {t('common.delete')}
            </Button>
          )}
          <Button
            icon={<ExportOutlined />}
            onClick={handleExport}
          >
            {t('common.export')}
          </Button>
        </Space>
      </div>

      <Card>
        <Descriptions title={t('planogramDetail.basicInfo')} bordered column={2}>
          <Descriptions.Item label={t('planogramDetail.code')}>{planogram.code}</Descriptions.Item>
          <Descriptions.Item label={t('planogramDetail.name')}>{planogram.name}</Descriptions.Item>
          <Descriptions.Item label={t('planogramDetail.status')}>
            <Tag color={STATUS_COLORS[planogram.status]}>
              {t(`storeGroup.planogram.status.${planogram.status.toLowerCase()}`)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('planogramDetail.layout')}>{planogram.layoutName}</Descriptions.Item>
          <Descriptions.Item label={t('planogramDetail.size')}>
            {`${planogram.width} x ${planogram.height}`}
          </Descriptions.Item>
          <Descriptions.Item label={t('planogramDetail.shelfCount')}>{planogram.shelfCount}</Descriptions.Item>
          <Descriptions.Item label={t('planogramDetail.productCount')}>{planogram.productCount}</Descriptions.Item>
          <Descriptions.Item label={t('planogramDetail.createdAt')}>{planogram.createdAt}</Descriptions.Item>
          <Descriptions.Item label={t('planogramDetail.updatedAt')}>{planogram.updatedAt}</Descriptions.Item>
          <Descriptions.Item label={t('planogramDetail.remark')} span={2}>
            {planogram.remark || '-'}
          </Descriptions.Item>
        </Descriptions>

        <Divider orientation="left">{t('planogramDetail.preview')}</Divider>

        {planogram && (
          <PlanogramEditor
            planogram={planogram}
            readOnly={true}
          />
        )}
      </Card>
    </div>
  );
};

export default PlanogramDetail;